import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import BrandMark from '@/components/brand/BrandMark';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, Mail, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const resetSchema = z.object({
  email: z.string().trim().email({ message: "Email inválido" }).max(255, { message: "Email deve ter menos de 255 caracteres" }),
});

type ResetFormData = z.infer<typeof resetSchema>;

export default function EsqueciSenha() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  
  const { resetPassword } = useAuth();
  const { toast } = useToast();

  const { register, handleSubmit, formState: { errors } } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema)
  });

  const onSubmit = async (data: ResetFormData) => {
    setIsLoading(true);
    setError('');
    setSuccess(false);

    try {
      const { error } = await resetPassword(data.email);

      if (error) {
        setError(error.message);
      } else {
        setSuccess(true);
        toast({
          title: "Email enviado!",
          description: "Verifique sua caixa de entrada para redefinir sua senha.",
        });
      }
    } catch (err: any) {
      setError(err.message || 'Erro inesperado');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md space-y-8">
        
        {/* Header com logo e título */}
        <div className="text-center space-y-6 animate-fade-in">
          <div className="flex justify-center mb-8">
            <BrandMark className="h-24 w-auto" />
          </div>
          
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight text-foreground">
              Recuperar Senha
            </h1>
            <p className="text-muted-foreground">
              Digite seu email para receber instruções de recuperação
            </p>
          </div>
        </div>
        
        {/* Card de recuperação */}
        <Card className="bg-card shadow-sm border rounded-3xl overflow-hidden animate-scale-in">
          <CardContent className="p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="p-2 rounded-xl bg-primary/10">
                  <Mail className="h-5 w-5 text-primary" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">Esqueci minha senha</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                Enviaremos um link de recuperação para seu email
              </p>
            </div>
            
            {!success ? (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div className="space-y-2">
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    {...register('email')}
                    disabled={isLoading}
                    className="h-12 rounded-xl border-border bg-background focus:bg-background transition-all duration-200 placeholder:text-muted-foreground/60"
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive animate-fade-in">{errors.email.message}</p>
                  )}
                </div>

                {error && (
                  <Alert variant="destructive" className="animate-fade-in">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <Button 
                  type="submit" 
                  className="w-full h-12 rounded-xl font-medium bg-primary hover:bg-primary/90 shadow-sm transition-all duration-200 text-primary-foreground"
                  disabled={isLoading}
                >
                  {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Enviar Link de Recuperação
                </Button>
              </form>
            ) : (
              <Alert className="animate-fade-in bg-primary/10 border-primary/20">
                <Mail className="h-4 w-4 text-primary" />
                <AlertDescription className="text-foreground">
                  <strong>Email enviado com sucesso!</strong>
                  <br />
                  Verifique sua caixa de entrada e clique no link para redefinir sua senha.
                </AlertDescription>
              </Alert>
            )}

            <div className="text-center pt-4 border-t border-border/20">
              <Link to="/login">
                <Button
                  variant="link"
                  disabled={isLoading}
                  className="text-muted-foreground hover:text-foreground text-sm transition-colors"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Voltar para o login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
